import { Faction, VictoryType, PenaltyType, PlayerRole, ROLE_FACTION } from '../../types/stats';
import { ScoreCalculation, PlayerInfo } from './types';

const BATCH_SIZE = 50;
const MAX_ITERATIONS = 10000;

export function getGoodCount(count: number): number {
  const map: Record<number, number> = {
    5: 3, 6: 4, 7: 4, 8: 5, 9: 6,
    10: 6, 11: 7, 12: 7, 13: 8
  };
  return map[count] || 5;
}

// V109: 获取邪恶阵营人数
export function getEvilCount(count: number): number {
  return count - getGoodCount(count);
}

export class StatsCalculator {
  private playerCount: number;
  private iterationCount: number;

  constructor(playerCount: number) {
    this.playerCount = playerCount;
    this.iterationCount = 0;
  }

  private checkIterationLimit(): void {
    this.iterationCount++;
    if (this.iterationCount > MAX_ITERATIONS) {
      throw new Error('迭代次数超过安全限制，可能存在死循环');
    }
  }

  private async yieldControl(): Promise<void> {
    if (this.iterationCount % BATCH_SIZE === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  private calculateBaseScore(isWinner: boolean, victoryType?: VictoryType): number {
    if (!isWinner) {
      return -this.playerCount;
    }
    
    if (victoryType === 'sabotage') {
      return 3 * this.playerCount;
    }
    
    return 2 * this.playerCount;
  }

  private calculateBonus(victoryType: VictoryType, isWinner: boolean): number {
    return isWinner && victoryType === 'assassin_fail' ? this.playerCount : 0;
  }

  private calculateMvpBonus(isMvp: boolean): number {
    if (!isMvp) return 0;
    return this.playerCount;
  }

  private calculatePenalty(penaltyType?: PenaltyType | null): number {
    if (!penaltyType) return 0;
    switch (penaltyType) {
      case 'self_destruct': return -2 * this.playerCount;
      case 'wrong_kill': return -this.playerCount;
      case 'bug': return -2 * this.playerCount;
      default: return 0;
    }
  }

  calculatePlayerScore(
    playerNumber: number,
    faction: Faction,
    winningFaction: Faction,
    victoryType: VictoryType,
    isMvp: boolean,
    penaltyType?: PenaltyType | null
  ): ScoreCalculation {
    const isWinner = faction === winningFaction;
    const baseScore = this.calculateBaseScore(isWinner, victoryType);
    const bonus = this.calculateBonus(victoryType, isWinner);
    const mvpBonus = this.calculateMvpBonus(isMvp);
    const penalty = this.calculatePenalty(penaltyType);

    console.log('[V283] 计算玩家得分:', {
      playerNumber,
      faction,
      winningFaction,
      victoryType,
      isMvp,
      playerCount: this.playerCount,
      baseScore,
      bonus,
      mvpBonus,
      penalty,
      totalScore: baseScore + bonus + mvpBonus + penalty
    });

    return {
      player_number: playerNumber,
      faction,
      base_score: baseScore + bonus,
      mvp_bonus: mvpBonus,
      penalty,
      total_score: baseScore + bonus + mvpBonus + penalty,
      is_mvp: isMvp,
      is_good_mvp: isMvp && faction === 'good',
      is_evil_mvp: isMvp && faction === 'evil',
    };
  }

  async calculateGameScoresAsync(
    playerInfos: PlayerInfo[],
    winningFaction: Faction,
    victoryType: VictoryType,
    mvpPlayer: number | ''
  ): Promise<ScoreCalculation[]> {
    const results: ScoreCalculation[] = [];

    for (let i = 0; i < playerInfos.length; i++) {
      this.checkIterationLimit();
      await this.yieldControl();

      const info = playerInfos[i];
      if (!info.player_role) continue;

      const faction = ROLE_FACTION[info.player_role as PlayerRole];
      const isMvp = mvpPlayer === info.player_number;

      const score = this.calculatePlayerScore(
        info.player_number,
        faction,
        winningFaction,
        victoryType,
        isMvp,
        null
      );
      results.push(score);
    }

    return results;
  }
}

export function calculateWinRate(wins: number, total: number): number {
  return total > 0 ? Math.round((wins / total) * 100) : 0;
}

export function calculateAvgScore(totalScore: number, totalGames: number): number {
  return totalGames > 0 ? Math.round(totalScore / totalGames) : 0;
}
